// src/components/fix-actions/artifact-detail-tray.tsx
// Full artifact tray: shows content, allows status change, regenerate, edit, deploy
import { useEffect, useState } from "react";
import {
  X,
  RefreshCw,
  Edit,
  Save,
  Send,
  ChevronDown,
  CheckCircle2,
  Clock,
  FileText,
  Sparkles,
  Copy,
  Check,
  Download,
  ArrowLeft,
} from "lucide-react";
import type { StrategyArtifact } from "../../services/fix-actions/optimization-studio-service";
import { fixActionsService, type DeployStatus } from "../../services/fix-actions/fix-actions-service";
import { FullContentRenderer } from "./full-content-renderer";
import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { Button, Textarea } from "../kit";
import { ClaimCheckBadge } from "./artifact-claim-check";

interface ArtifactDetailTrayProps {
  artifact: StrategyArtifact;
  brandId: string;
  onClose: () => void;
  onRegenerate: (artifactId: string) => Promise<StrategyArtifact | null>;
  onStatusChange: (artifactId: string, status: DeployStatus) => void;
  isAssistantCollapsed?: boolean;
}

const STATUS_OPTIONS: DeployStatus[] = ["Draft", "In Review", "Ready", "Published"];

function getStatusColors(status: string) {
  const s = status.toLowerCase();
  if (s === "published") return { bg: "var(--alpha-teal-10)", text: "var(--alpha-teal)", border: "var(--alpha-teal-20)" };
  if (s === "ready") return { bg: "var(--success-bg)", text: "var(--success-text)", border: "var(--success-border)" };
  if (s === "in review" || s === "in_review") return { bg: "var(--warning-bg)", text: "var(--warning-text)", border: "var(--warning-border)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

function getStatusIcon(status: string) {
  const s = status.toLowerCase();
  if (s === "published") return <CheckCircle2 className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />;
  if (s === "ready") return <CheckCircle2 className="w-4 h-4" style={{ color: "var(--success-text)" }} />;
  if (s === "in review" || s === "in_review") return <Clock className="w-4 h-4" style={{ color: "var(--warning-text)" }} />;
  return <FileText className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />;
}

function getArtifactTypeColor(type: string): string {
  const MAP: Record<string, string> = {
    "Comparison Asset": "var(--alpha-teal)",
    "Decision Framework": "var(--signal-blue)",
    "Scenario-Based Q&A": "var(--spectral-violet)",
    "Boundary / Trust Artifact": "var(--infrared-coral)",
    "Evidence & Citation Pack": "var(--chart-3)",
    "Structured Data / Schema": "var(--muted-foreground)",
    "Data Artifact": "var(--chart-2)",
  };
  return MAP[type] || "var(--muted-foreground)";
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function flattenToText(obj: any, depth = 0): string {
  const lines: string[] = [];
  const indent = "  ".repeat(depth);
  for (const [k, v] of Object.entries(obj || {})) {
    if (typeof v === "string" && v.trim()) lines.push(`${indent}${k}: ${v}`);
    else if (Array.isArray(v)) {
      lines.push(`${indent}${k}:`);
      v.forEach((item) => {
        if (typeof item === "string") lines.push(`${indent}  - ${item}`);
        else if (typeof item === "object") lines.push(flattenToText(item, depth + 2));
      });
    } else if (typeof v === "object" && v !== null) {
      lines.push(`${indent}${k}:`);
      lines.push(flattenToText(v, depth + 1));
    }
  }
  return lines.join("\n");
}

function StatusSelector({
  current,
  onChange,
  saving,
}: {
  current: string;
  onChange: (s: DeployStatus) => void;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);
  const colors = getStatusColors(current);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-weight-semibold)",
          cursor: "pointer",
        }}
      >
        {getStatusIcon(current)}
        {current}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute z-50 mt-1 rounded-lg shadow-lg overflow-hidden"
            style={{
              backgroundColor: "var(--popover)",
              border: "1px solid var(--border)",
              minWidth: "160px",
              boxShadow: "var(--elevation-md)",
              top: "100%",
              left: 0,
            }}
          >
            {STATUS_OPTIONS.filter((s) => s !== "Published").map((s) => {
              const c = getStatusColors(s);
              const isCurrent = s === current;
              return (
                <button
                  key={s}
                  onClick={() => {
                    setOpen(false);
                    if (!isCurrent) onChange(s);
                  }}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-muted transition-colors"
                  style={{
                    backgroundColor: isCurrent ? c.bg : "transparent",
                    color: isCurrent ? c.text : "var(--foreground)",
                    fontSize: "var(--text-sm)",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {getStatusIcon(s)}
                  {s}
                  {isCurrent && <CheckCircle2 className="w-3.5 h-3.5 ml-auto" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function ArtifactDetailTray({
  artifact: initialArtifact,
  brandId,
  onClose,
  onRegenerate,
  onStatusChange,
  isAssistantCollapsed = false,
}: ArtifactDetailTrayProps) {
  const { isDesktop } = useResponsive();

  const [artifact, setArtifact] = useState<StrategyArtifact>(initialArtifact);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(
    JSON.stringify(initialArtifact.fullContent || {}, null, 2)
  );
  const [editError, setEditError] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveConfirmed, setSaveConfirmed] = useState(false);

  useEffect(() => {
    setArtifact(initialArtifact);
    setEditedContent(JSON.stringify(initialArtifact.fullContent || {}, null, 2));
  }, [initialArtifact.id]);

  const typeColor = getArtifactTypeColor(String(artifact.type));
  const safeFilename = (artifact.name || "asset").replace(/[^a-z0-9_-]/gi, "_");

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(artifact.fullContent, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleDownloadJson = () => {
    downloadBlob(JSON.stringify(artifact.fullContent, null, 2), `${safeFilename}.json`, "application/json");
  };

  const handleDownloadText = () => {
    downloadBlob(flattenToText(artifact.fullContent), `${safeFilename}.txt`, "text/plain");
  };

  const handleSaveEdit = () => {
    setEditError(null);
    try {
      const parsed = JSON.parse(editedContent);
      setArtifact((prev) => ({ ...prev, fullContent: parsed }));
      setIsEditing(false);
      setSaveConfirmed(true);
      setTimeout(() => setSaveConfirmed(false), 2000);
    } catch {
      setEditError("Invalid JSON — please fix before saving.");
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const updated = await onRegenerate(artifact.id);
      if (updated) {
        setArtifact(updated);
        setEditedContent(JSON.stringify(updated.fullContent || {}, null, 2));
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleStatusChange = async (newStatus: DeployStatus) => {
    if (newStatus === "Published") {
      // #133: sends the artifact to Brand Publisher; Published comes from the
      // publication record when it is published there.
      onStatusChange(artifact.id, newStatus);
      return;
    }
    setIsSavingStatus(true);
    try {
      const deployLog = await fixActionsService.getDeployLog({ brandId });
      const entry = deployLog.items.find((e) => e.artifactId === artifact.id);
      if (entry) {
        await fixActionsService.patchDeployLogStatus({
          id: entry.id,
          status: newStatus,
          brandId,
        });
      }
      setArtifact((prev) => ({ ...prev, status: newStatus }));
      onStatusChange(artifact.id, newStatus);
    } finally {
      setIsSavingStatus(false);
    }
  };

  // Responsive tray sizing:
  //  - <lg (1024px): full-width overlay; AI sidebar is hidden below lg so
  //    we don't need to reserve the 380px offset.
  //  - >=lg: original right-docked tray (52%, min-width 620px), with right
  //    offset depending on sidebar state.
  const trayPosStyle: React.CSSProperties = isDesktop
    ? {
        right: isAssistantCollapsed ? "0" : "380px",
        left: "auto",
        width: "52%",
        minWidth: "620px",
        borderLeft: "1px solid var(--border)",
      }
    : {
        right: 0,
        left: 0,
        width: "100%",
        minWidth: 0,
        borderLeft: "none",
      };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-30"
        style={{ backdropFilter: "blur(2px)" }}
        onClick={onClose}
      />

      {/* Tray */}
      <div
        className="fixed top-0 bottom-0 z-40 flex flex-col shadow-2xl"
        style={{
          backgroundColor: "var(--background)",
          animation: "slideInRight 0.3s ease-out",
          ...trayPosStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
        >
          <div className="flex items-start justify-between mb-3 gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                <h2
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {artifact.name}
                </h2>
                <span
                  className="px-2 py-0.5 rounded shrink-0"
                  style={{
                    backgroundColor: `${typeColor}10`,
                    color: typeColor,
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-weight-medium)",
                    border: `1px solid ${typeColor}30`,
                  }}
                >
                  {String(artifact.type)}
                </span>
                {saveConfirmed && (
                  <span
                    className="px-2 py-0.5 rounded shrink-0"
                    style={{
                      backgroundColor: "var(--success-bg)",
                      color: "var(--success-text)",
                      fontSize: "var(--text-xs)",
                      border: "1px solid var(--success-border)",
                    }}
                  >
                    ✓ Saved
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    overflowWrap: "anywhere",
                  }}
                >
                  {artifact.supportingActionTitle}
                </span>
                {artifact.strategicTheme && (
                  <>
                    <span style={{ color: "var(--muted-foreground)" }}>·</span>
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                      {artifact.strategicTheme}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors shrink-0"
              style={{
                color: "var(--muted-foreground)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-3 md:gap-4 flex-wrap">
            <StatusSelector
              current={String(artifact.status || "Draft")}
              onChange={handleStatusChange}
              saving={isSavingStatus}
            />
            <ClaimCheckBadge check={artifact.claimCheck} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              Generated:{" "}
              {artifact.generatedAt
                ? new Date(artifact.generatedAt).toLocaleDateString()
                : "—"}
            </span>
          </div>
        </div>

        {String(artifact.status).toLowerCase() === "published" && (
          <div
            className="flex-shrink-0 px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3"
            style={{
              backgroundColor: "var(--alpha-teal-10)",
              borderBottom: "1px solid var(--alpha-teal-20)",
            }}
          >
            <CheckCircle2
              className="w-5 h-5 shrink-0"
              style={{ color: "var(--alpha-teal)" }}
            />
            <p
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--alpha-teal)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              This asset has been published. The associated action is now marked as completed.
            </p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 space-y-6">
          {artifact.intent && (
            <div>
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "8px",
                }}
              >
                Strategic Intent
              </div>
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  lineHeight: "1.6",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {artifact.intent}
              </p>
            </div>
          )}

          {artifact.aiReasoningBehavior && (
            <div
              className="p-4 rounded-lg"
              style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
            >
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "6px",
                }}
              >
                AI Reasoning Behavior Influenced
              </div>
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  lineHeight: "1.6",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {artifact.aiReasoningBehavior}
              </p>
            </div>
          )}

          {artifact.deploymentTargets && artifact.deploymentTargets.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "8px",
                }}
              >
                Deployment Targets
              </div>
              <div className="flex flex-wrap gap-2">
                {artifact.deploymentTargets.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-1 rounded"
                    style={{
                      backgroundColor: "var(--muted)",
                      fontSize: "var(--text-xs)",
                      color: "var(--foreground)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Full Content
              </div>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                {isEditing ? "Editing raw JSON" : "Rendered view"}
              </span>
            </div>

            {isEditing ? (
              <div>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="upm-code-area"
                  state={editError ? "error" : undefined}
                  />
                {editError && (
                  <p
                    style={{
                      fontSize: "var(--text-xs)",
                      color: "var(--error-text)",
                      marginTop: "4px",
                    }}
                  >
                    {editError}
                  </p>
                )}
              </div>
            ) : (
              <div
                className="p-4 md:p-6 rounded-lg"
                style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}
              >
                {artifact.fullContent && Object.keys(artifact.fullContent).length > 0 ? (
                  <FullContentRenderer content={artifact.fullContent} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Sparkles
                      className="w-10 h-10 mb-3"
                      style={{ color: "var(--muted-foreground)" }}
                    />
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                      No content available. Try regenerating this asset.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {artifact.generatedUsing && (
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Generated using: {artifact.generatedUsing}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex-shrink-0 border-t"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
        >
          {isEditing ? (
            <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-end gap-2 flex-wrap">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setEditError(null);
                }}
              >
                Cancel
              </Button>
              <button
                onClick={handleSaveEdit}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md"
                style={{
                  backgroundColor: "var(--signal-blue)",
                  color: "white",
                  fontWeight: "var(--font-weight-semibold)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          ) : (
            <>
              <div className="px-4 md:px-6 lg:px-8 pt-3 pb-1 flex items-center gap-1 flex-wrap">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(true);
                    setEditError(null);
                  }}
                  icon={Edit}
                >Edit
                </Button>

                <Button variant="ghost" onClick={handleCopy}>
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? "Copied!" : "Copy JSON"}
                </Button>

                <Button
                  variant="ghost"
                  onClick={handleDownloadJson}
                  icon={Download}
                >JSON
                </Button>

                <Button
                  variant="ghost"
                  onClick={handleDownloadText}
                  icon={Download}
                >TXT
                </Button>
              </div>

              <div className="px-4 md:px-6 lg:px-8 pb-3 md:pb-4 flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-2">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="self-start"
                  icon={ArrowLeft}
                >Back
                </Button>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="secondary"
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                    className="flex-1 sm:flex-none"
                  >
                    {isRegenerating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Regenerating…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                      </>
                    )}
                  </Button>

                  {String(artifact.status).toLowerCase() !== "published" && (
                    <button
                      onClick={() => handleStatusChange("Published")}
                      disabled={isSavingStatus}
                      className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md flex-1 sm:flex-none"
                      style={{
                        backgroundColor: "var(--alpha-teal)",
                        color: "white",
                        fontWeight: "var(--font-weight-semibold)",
                        border: "none",
                        cursor: "pointer",
                        opacity: isSavingStatus ? 0.7 : 1,
                      }}
                    >
                      <Send className="w-4 h-4" />
                      Send to Brand Publisher
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}